import { apiClient } from './client';
import { CursorPage, DMConversation, DirectMessage, Message, MessageType } from '@/types';

export const messagesApi = {
  async getChannelMessages(
    channelId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<CursorPage<Message>> {
    const res = await apiClient.get<CursorPage<Message>>(
      `/api/messages/channel/${channelId}`,
      { params },
    );
    return res.data;
  },

  async sendChannelMessage(
    channelId: string,
    data: {
      content: string;
      type?: MessageType;
      replyToId?: string;
      silent?: boolean;
      scheduledFor?: string;
      viewOnce?: boolean;
      clientMessageId?: string;
      mediaFileId?: string;
    },
  ): Promise<Message> {
    const res = await apiClient.post<Message>(`/api/messages/channel/${channelId}`, data);
    return res.data;
  },

  async pinMessage(messageId: string): Promise<Message> {
    const res = await apiClient.post<Message>(`/api/messages/${messageId}/pin`);
    return res.data;
  },

  async unpinMessage(messageId: string): Promise<Message> {
    const res = await apiClient.post<Message>(`/api/messages/${messageId}/unpin`);
    return res.data;
  },

  async listPinned(channelId: string): Promise<Message[]> {
    const res = await apiClient.get<Message[]>(`/api/messages/channel/${channelId}/pinned`);
    return res.data;
  },

  async forwardMessage(
    messageId: string,
    target: { toChannelId?: string; toPeerUserId?: string; silent?: boolean },
  ): Promise<Message> {
    const res = await apiClient.post<Message>(`/api/messages/${messageId}/forward`, target);
    return res.data;
  },

  async consumeViewOnce(messageId: string): Promise<Message> {
    const res = await apiClient.post<Message>(`/api/messages/${messageId}/view-once`);
    return res.data;
  },

  async getDMs(
    userId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<CursorPage<DirectMessage>> {
    const res = await apiClient.get<CursorPage<DirectMessage>>(`/api/messages/dm/${userId}`, { params });
    return res.data;
  },

  async sendDM(
    recipientId: string,
    data: {
      content: string;
      type?: MessageType;
      clientMessageId?: string;
      replyToId?: string;
      mediaFileId?: string;
      scheduledFor?: string;
      viewOnce?: boolean;
    },
  ): Promise<DirectMessage> {
    const res = await apiClient.post<DirectMessage>(`/api/messages/dm/${recipientId}`, data);
    return res.data;
  },

  async getDMConversations(): Promise<DMConversation[]> {
    const res = await apiClient.get<DMConversation[]>('/api/messages/dm');
    return res.data;
  },

  async editMessage(messageId: string, content: string): Promise<Message> {
    const res = await apiClient.put<Message>(`/api/messages/${messageId}`, { content });
    return res.data;
  },

  async deleteMessage(messageId: string): Promise<void> {
    await apiClient.delete(`/api/messages/${messageId}`);
  },

  // Phase 16 — DM-specific удаление и очистка.
  // scope: 'me'  = скрыть у себя (через deletedForUserIds).
  // scope: 'all' = пометить удалённым у всех (только автору).
  async deleteDm(dmId: string, scope: 'me' | 'all'): Promise<void> {
    await apiClient.delete(`/api/messages/dm/${dmId}`, { data: { scope } });
  },

  async clearDmChat(peerId: string, scope: 'me' | 'all'): Promise<{ affected: number }> {
    const res = await apiClient.post(`/api/messages/dm/${peerId}/clear`, { scope });
    return res.data;
  },

  // ── Bulk (multi-select) ────────────────────────────────────────────────
  async bulkDelete(payload: { messageIds?: string[]; dmIds?: string[] }): Promise<{
    deleted: number;
    failed: Array<{ id: string; error: string }>;
  }> {
    const res = await apiClient.post('/api/messages/bulk-delete', payload);
    return res.data;
  },

  async bulkForward(payload: {
    messageIds: string[];
    toChannelId?: string;
    toPeerUserId?: string;
    silent?: boolean;
  }): Promise<{ forwarded: number; failed: Array<{ id: string; error: string }> }> {
    const res = await apiClient.post('/api/messages/bulk-forward', payload);
    return res.data;
  },

  async bulkSaveToPersonal(payload: { messageIds?: string[]; dmIds?: string[] }): Promise<{
    channelId: string;
    saved: number;
    failed: Array<{ id: string; error: string }>;
  }> {
    const res = await apiClient.post('/api/messages/bulk-save-to-personal', payload);
    return res.data;
  },
};
